import type { EvaluationCheck, GameplayEvaluationReceipt } from "../shared/adapter";
import type { TopDownCollectSpec } from "./spec";

interface Point { readonly x: number; readonly y: number; readonly r: number }

function layout(spec: TopDownCollectSpec): { collectibles: Point[]; hazards: Point[] } {
  let state = spec.seed >>> 0;
  const random = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);
  const make = (count: number, radius: number) => Array.from({ length: count }, () => ({
    x: radius + random() * (spec.world.width - radius * 2),
    y: radius + random() * (spec.world.height - radius * 2),
    r: radius,
  }));
  return { collectibles: make(spec.world.goalCollectibles, 11), hazards: make(spec.world.hazards, 18) };
}

function distance(a: Point, b: Point): number { return Math.hypot(a.x - b.x, a.y - b.y); }

/**
 * Plays the same seeded layout used by the trusted compiler. The evaluator
 * follows a deterministic nearest-collectible keyboard route, verifies the
 * touch route, checks collision spawn safety, and records replay evidence.
 *
 * Same algorithm as the pre-registry evaluator; only the trace's wire shape
 * changed, to the generic `{elapsedSeconds, action}` every kind now shares.
 */
export function evaluateTopDownCollect(spec: TopDownCollectSpec): GameplayEvaluationReceipt {
  const generated = layout(spec);
  const unsafeCollectibles = generated.collectibles.filter((item) =>
    generated.hazards.some((hazard) => distance(item, hazard) < item.r + hazard.r),
  ).length;
  const remaining = generated.collectibles.map((point, index) => ({ point, index }));
  let cursor: Point = { x: spec.world.width / 2, y: spec.world.height / 2, r: 18 };
  let routeDistance = 0;
  const trace: { readonly elapsedSeconds: number; readonly action: { readonly type: "move-to"; readonly to: readonly [number, number] } }[] = [];
  while (remaining.length) {
    remaining.sort((a, b) => distance(cursor, a.point) - distance(cursor, b.point) || a.index - b.index);
    const next = remaining.shift()!;
    routeDistance += distance(cursor, next.point);
    trace.push({
      elapsedSeconds: Number((routeDistance / spec.player.speed).toFixed(3)),
      action: { type: "move-to", to: [Number(next.point.x.toFixed(3)), Number(next.point.y.toFixed(3))] },
    });
    cursor = next.point;
  }
  const keyboardSeconds = routeDistance / spec.player.speed;
  const keyboardPassed = keyboardSeconds * 1.2 <= spec.world.timeLimitSeconds && unsafeCollectibles === 0;
  const touchPassed = unsafeCollectibles === 0;
  const estimatedFrameOperations = spec.world.goalCollectibles + spec.world.hazards + 20;
  const framePassed = estimatedFrameOperations <= 120;
  const checks: EvaluationCheck[] = [
    { id: "layout-safe", passed: unsafeCollectibles === 0, evidence: `${unsafeCollectibles} collectible-hazard spawn overlaps` },
    { id: "keyboard-goal", passed: keyboardPassed, evidence: `${keyboardSeconds.toFixed(3)}s route inside ${spec.world.timeLimitSeconds}s budget with 20% control margin` },
    { id: "touch-goal", passed: touchPassed, evidence: touchPassed ? "every seeded collectible has a collision-safe touch target" : "one or more touch targets collide with hazards" },
    { id: "frame-budget", passed: framePassed, evidence: `${estimatedFrameOperations} estimated primitive operations per frame` },
  ];
  return {
    version: 1,
    seed: spec.seed,
    passed: checks.every((check) => check.passed),
    checks,
    trace,
    metrics: {
      routeDistance: Number(routeDistance.toFixed(3)),
      keyboardSeconds: Number(keyboardSeconds.toFixed(3)),
      estimatedFrameOperations,
    },
  };
}
